import { apiFetch } from './api';

/**
 * Admin dashboard data layer for Pack list / detail reads.
 *
 * Source of truth for the row shape is the Worker's D1 `packs` table; see
 * `infra/worker/src/db/types.ts`. Field names mirror the Worker response
 * exactly (snake_case) so we don't have to maintain a parallel mapping —
 * `apiFetch` returns the JSON body verbatim.
 *
 * Validates Requirements 21.1, 21.2, 21.3, 21.4, 21.5.
 */

/** Mirrors the SQL CHECK constraint on `packs.kind`. */
export type PackKind = 'sticker' | 'gif';

/** Mirrors the SQL CHECK constraint on `packs.pack_state`. */
export type PackState = 'draft' | 'published' | 'unpublished';

/**
 * One row of the pack list. Fields are snake_case to match the Worker's
 * D1 row projection; see `PackRow` in `infra/worker/src/db/types.ts`.
 */
export interface PackRow {
  id: string;
  kind: PackKind;
  name: string;
  description: string | null;
  cover_asset_id: string | null;
  pack_state: PackState;
  active_version: number;
  next_version: number;
  /** ISO-8601 UTC string; `null` until first successful publish. */
  published_at: string | null;
  /** ISO-8601 UTC string; always present. */
  updated_at: string;
  /** Hex sha256 of the active manifest body; `null` until first publish. */
  manifest_sha256: string | null;
  /** Price in VND, 0 = free. */
  price_vnd: number;
}

/**
 * One page of pack rows plus the opaque cursor for the next page. The
 * cursor is `null` when the caller has reached the end.
 *
 * The cursor is opaque from the dashboard's perspective: encode/decode
 * lives entirely on the Worker side. We pass it back through unchanged.
 */
export interface PackListPage {
  items: PackRow[];
  nextCursor: string | null;
}

/** Query knobs accepted by {@link listPacks}. */
export interface PackListFilters {
  kind?: PackKind;
  state?: PackState;
  cursor?: string;
  /** Defaults to 25 to match the design's pagination spec (Req 21.5). */
  limit?: number;
}

/** Page size baked into the design; surfaced for callers that want to be explicit. */
export const PACK_LIST_PAGE_SIZE = 25;

/**
 * Fetch one page of packs from `GET /admin/v1/packs`.
 *
 * Filters and pagination are passed through as query-string parameters.
 * Empty / undefined values are omitted so the URL stays clean — the Worker
 * treats a missing param as "no filter".
 *
 * Throws `ApiError` on non-2xx (the global error boundary surfaces it).
 *
 * _Requirements: 21.1, 21.5_
 */
export async function listPacks(
  filters: PackListFilters = {},
): Promise<PackListPage> {
  const params = new URLSearchParams();
  if (filters.kind) {
    params.set('kind', filters.kind);
  }
  if (filters.state) {
    params.set('state', filters.state);
  }
  if (filters.cursor) {
    params.set('cursor', filters.cursor);
  }
  params.set('limit', String(filters.limit ?? PACK_LIST_PAGE_SIZE));

  const qs = params.toString();
  return apiFetch<PackListPage>(`/admin/v1/packs?${qs}`);
}


/**
 * Pack detail returned by `GET /admin/v1/packs/{id}`.
 *
 * The Worker projection adds `nextVersion` (computed as
 * `max(active_version + 1, next_version)`) on top of the bare {@link PackRow}.
 * The list endpoint does NOT include this field; it is editor-specific
 * (Requirement 23.1: publish dialog shows the target version).
 *
 * The Worker does not yet attach an `assets` array to this response —
 * the editor fetches assets separately via `GET /admin/v1/packs/{id}/assets`.
 */
export interface PackDetail extends PackRow {
  nextVersion: number;
}

/**
 * Body accepted by `POST /admin/v1/packs`.
 *
 * The Worker only validates the create-time fields (`kind`, `name`,
 * optional `description`, optional `coverAssetId`); it does not yet
 * accept `tags` or a free-form `coverImage` URL. Extra keys are
 * silently ignored. Keep this shape narrow so a typo on the dashboard
 * side is caught at compile time.
 */
export interface CreatePackBody {
  kind: PackKind;
  name: string;
  description?: string;
  coverAssetId?: string;
  priceVnd?: number;
}

/**
 * Body accepted by `PATCH /admin/v1/packs/{id}`.
 *
 * The Worker route rejects any unknown key (`kind`, `tags`, `coverImage`,
 * etc.) with 400 `validation_failed`, so the editor strips fields that
 * the Worker does not yet support before sending. Documented on
 * `PackMetadataInput` in `@skey/manifest`.
 */
export interface PatchPackBody {
  name?: string;
  description?: string | null;
  coverAssetId?: string | null;
  priceVnd?: number;
}

/**
 * Fetch a single pack with its derived `nextVersion` counter.
 *
 * _Requirements: 22.1, 23.1_
 */
export async function getPack(id: string): Promise<PackDetail> {
  return apiFetch<PackDetail>(`/admin/v1/packs/${encodeURIComponent(id)}`);
}

/**
 * Create a new draft pack and return the persisted row.
 *
 * _Requirements: 15.2, 22.1_
 */
export async function createPack(body: CreatePackBody): Promise<PackRow> {
  return apiFetch<PackRow>('/admin/v1/packs', {
    method: 'POST',
    body,
  });
}

/**
 * Patch metadata on an existing pack and return the updated row.
 *
 * The Worker bumps `updated_at` on every successful PATCH (even on an
 * empty body), so a "touch" call is a valid no-op refresh.
 *
 * _Requirements: 22.1_
 */
export async function patchPack(
  id: string,
  body: PatchPackBody,
): Promise<PackDetail> {
  return apiFetch<PackDetail>(`/admin/v1/packs/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  });
}

/**
 * Delete a pack, including published ones if `force=true`. Cascades
 * through the assets table on the Worker side; the editor should refresh
 * the list after a successful call.
 *
 * _Requirements: 22.4_
 */
export async function deletePack(id: string, options: { force?: boolean } = {}): Promise<void> {
  const qs = options.force ? '?force=true' : '';
  await apiFetch<null>(`/admin/v1/packs/${encodeURIComponent(id)}${qs}`, {
    method: 'DELETE',
  });
}

/**
 * Response shape from `POST /admin/v1/packs/{id}/publish`.
 *
 * The Worker returns the updated pack row plus four derived fields
 * (`version`, `manifestSha256`, `assetCount`, `totalBytes`) so the
 * editor can render the success toast without an extra fetch. We type
 * them all as required because every successful publish includes them.
 */
export interface PublishResponse extends PackRow {
  version: number;
  manifestSha256: string;
  assetCount: number;
  totalBytes: number;
}

/**
 * Publish the current draft. The Worker performs the canonical-encode +
 * sha256 + R2 copy + catalog rebuild atomically and returns the new
 * version on success.
 *
 * _Requirements: 23.1, 23.2, 23.3_
 */
export async function publishPack(id: string): Promise<PublishResponse> {
  return apiFetch<PublishResponse>(
    `/admin/v1/packs/${encodeURIComponent(id)}/publish`,
    { method: 'POST' },
  );
}

/**
 * Unpublish a published pack. The Worker keeps the version's R2 objects
 * around for any iOS clients still on a stale catalog snapshot.
 *
 * _Requirements: 23.4_
 */
export async function unpublishPack(id: string): Promise<PackRow> {
  return apiFetch<PackRow>(
    `/admin/v1/packs/${encodeURIComponent(id)}/unpublish`,
    { method: 'POST' },
  );
}
